import { useEffect, useMemo, useState } from 'react';

import { useAtom } from 'jotai';

import { track } from '../../../../../utils/tracking';
import { kpiAtom } from '../../../../atoms';
import { validateNonNegativeNumber } from '../../../../utils/formValidators';
import { constraintsAtom, invalidFormAtom, isDirtyAtom } from '../../atoms';
import { KPI_METRIC_TEXT } from '../../constants';
import { CONSTRAINT_CONSTANTS, FIXED_SPEND_CHANNELS } from '../constants';

export const calculateConstrainedSpend = (currentSpend, percentage) => {
  // Calculate min/max spend using percentage range, then round down to nearest $100
  const min = Math.floor((currentSpend * (1 - percentage)) / 100) * 100;
  const max = Math.floor((currentSpend * (1 + percentage)) / 100) * 100;
  return { min, max };
};

const shouldChannelAllowChange = key => FIXED_SPEND_CHANNELS.some(channel => key.toLowerCase().includes(channel));

const getConstrainedValues = (channel, currentSpend, percentage) => {
  if (currentSpend === 0) {
    return {
      min_value: 0,
      max_value: 0,
    };
  }

  if (shouldChannelAllowChange(channel)) {
    return {
      min_value: currentSpend,
      max_value: currentSpend,
    };
  }

  if (percentage === false) {
    return {
      min_value: CONSTRAINT_CONSTANTS.MIN_VALUE,
      max_value: CONSTRAINT_CONSTANTS.MAX_VALUE,
    };
  }

  const { min, max } = calculateConstrainedSpend(currentSpend, percentage);
  return {
    min_value: min,
    max_value: max,
  };
};

const computeInitialConstraints = (featureStats, percentage = false) => Object.entries(featureStats)
  .reduce((constraints, [channel, stats]) => {
    const currentSpend = stats?.currentSpend;
    const defaultConstraints = {
      originalSpend: currentSpend,
    };

    return {
      ...constraints,
      [channel]: {
        ...defaultConstraints,
        ...getConstrainedValues(channel, currentSpend, percentage),
      },
    };
  }, {});

export const useConstraints = ({ featureStats }) => {
  const [constraints, setConstraints] = useAtom(constraintsAtom);
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [percentage, setPercentage] = useState(false);

  useEffect(() => {
    if (!featureStats) return;
    const computedConstraints = computeInitialConstraints(featureStats);
    setConstraints(computedConstraints);
  }, [featureStats]);

  const setConstraintPercentage = (updatedPercentage, customFeatureSettings) => {
    setPercentage(updatedPercentage);
    const customFeatureNames = customFeatureSettings.map(f => f.featureName);

    const updatedConstraints = Object.entries(featureStats).reduce((acc, [channel, stats]) => {
      if (customFeatureNames.includes(channel)) {
        return {
          ...acc,
          [channel]: constraints[channel],
        };
      }

      const currentSpend = stats?.currentSpend;

      return {
        ...acc,
        [channel]: {
          ...constraints[channel],
          ...getConstrainedValues(channel, currentSpend, updatedPercentage),
        },
      };
    }, {});

    setConstraints(updatedConstraints);
    setIsDirty(true);
  };

  return {
    constraints,
    setConstraints,
    percentage,
    setConstraintPercentage,
  };
};

export const useCustomFeatureConstraints = ({ constraints, setConstraints, percentage }) => {
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [customFeatureSettings, setCustomFeatureSettings] = useState([]);

  const updateFeatureConstraints = (featureName, featurePercentage) => {
    setIsDirty(true);
    const currentSpend = constraints[featureName]?.originalSpend || 0;

    setConstraints({
      ...constraints,
      [featureName]: {
        ...constraints[featureName],
        ...getConstrainedValues(featureName, currentSpend, featurePercentage),
      },
    });
  };

  const handleAddFeature = (e, { value }) => {
    setIsDirty(true);
    const featurePercentage = percentage || CONSTRAINT_CONSTANTS.DEFAULT_PERCENTAGE;

    setCustomFeatureSettings([
      ...customFeatureSettings,
      {
        featureName: value,
        percentage: featurePercentage,
        mode: 'percentage',
      },
    ]);

    updateFeatureConstraints(value, featurePercentage);
    track('mmm.scenario_planner.add_feature_constraint', { featureName: value });
  };

  const setFeaturePercentage = (featureName, value) => {
    const updatedCustomFeatureSettings = customFeatureSettings
      .map(feature => (feature.featureName === featureName
        ? { ...feature, percentage: value }
        : feature));
    setCustomFeatureSettings(updatedCustomFeatureSettings);
    updateFeatureConstraints(featureName, value);
  };

  const setFeatureMode = (featureName, mode) => {
    const updatedCustomFeatureSettings = customFeatureSettings
      .map(feature => (feature.featureName === featureName
        ? { ...feature, mode }
        : feature));
    setCustomFeatureSettings(updatedCustomFeatureSettings);
  };

  const handleRemoveFeature = featureNameToRemove => {
    setIsDirty(true);
    setCustomFeatureSettings(customFeatureSettings.filter(
      f => f.featureName !== featureNameToRemove,
    ));
    updateFeatureConstraints(featureNameToRemove, percentage);
  };

  return {
    customFeatureSettings,
    setCustomFeatureSettings,
    handleAddFeature,
    handleRemoveFeature,
    setFeaturePercentage,
    setFeatureMode,
  };
};

export const useConstraintTable = ({ budgetPeriodName }) => {
  const [kpi] = useAtom(kpiAtom);
  const isConversionKPI = kpi === KPI_METRIC_TEXT.conversions;
  const metricText = isConversionKPI ? KPI_METRIC_TEXT.CPA : KPI_METRIC_TEXT.ROAS;

  const tableHeaders = useMemo(() => ([
    { key: 'feature', content: 'Feature' },
    { key: 'percentChange', content: '% Change' },
    { key: 'minSpend', content: `Min ${budgetPeriodName} Spend` },
    { key: 'maxSpend', content: `Max ${budgetPeriodName} Spend` },
    { key: 'target', content: `${metricText} Target (Optional)` },
    { key: 'actions', content: '' },
  ]), [isConversionKPI, budgetPeriodName]);

  return {
    tableHeaders,
  };
};

export const useConstraintInputCells = ({
  budgetPeriod,
  featureName,
  mode,
  constraints,
  setConstraints,
}) => {
  const [kpi] = useAtom(kpiAtom);
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [, setInvalidForm] = useAtom(invalidFormAtom);
  const isConversionKPI = kpi === KPI_METRIC_TEXT.conversions;
  const metricText = isConversionKPI ? KPI_METRIC_TEXT.CPA : KPI_METRIC_TEXT.ROAS;
  const { min_value: minValue, max_value: maxValue, min_roas: minRoas } = constraints[featureName];

  const [displayMin, setDisplayMin] = useState(Math.round(minValue * budgetPeriod));
  const [displayMax, setDisplayMax] = useState(Math.round(maxValue * budgetPeriod));
  const [displayRoas, setDisplayRoas] = useState(minRoas && isConversionKPI ? 1 / minRoas : minRoas);
  const [minIssue, setMinIssue] = useState(false);
  const [maxIssue, setMaxIssue] = useState(false);
  const [roasIssue, setRoasIssue] = useState(false);

  useEffect(() => {
    const calculatedMin = Math.round(minValue * budgetPeriod);
    const calculatedMax = Math.round(maxValue * budgetPeriod);

    if (displayMin !== calculatedMin) {
      setDisplayMin(calculatedMin);
    }
    if (displayMax !== calculatedMax) {
      setDisplayMax(calculatedMax);
    }
  }, [minValue, maxValue, budgetPeriod]);

  const validateInputValue = (value, minMaxValidator) => {
    const numberError = validateNonNegativeNumber(value) ? false : 'Must be a valid and positive number';
    return numberError || (minMaxValidator ? false : 'Minimum value cannot be greater than maximum value');
  };

  const onChangeHandler = (
    field,
    displaySetter,
    issueSetter,
    validator,
    valueTransformer,
  ) => (e, { value }) => {
    setIsDirty(true);
    displaySetter(value);

    const error = validateInputValue(value, validator(value));
    if (error) {
      issueSetter(error);
      setInvalidForm(true);
    } else {
      issueSetter(false);
      setInvalidForm(false);
      setConstraints({
        ...constraints,
        [featureName]: {
          ...constraints[featureName],
          [field]: valueTransformer(value),
        },
      });
    }
    track('mmm.scenario_planner.edit_refine_constraints', { featureName, featureValue: field });
  };
  const inputCells = [
    {
      key: 'min_value',
      value: displayMin,
      placeholder: 'Min Spend',
      onChange: onChangeHandler(
        'min_value',
        setDisplayMin,
        setMinIssue,
        value => value <= maxValue * budgetPeriod,
        value => value / budgetPeriod,
      ),
      error: !!minIssue,
      errorMessage: minIssue,
      icon: 'dollar',
      iconPosition: 'left',
      disabled: mode === 'percentage',
    },
    {
      key: 'max_value',
      value: displayMax,
      placeholder: 'Max spend',
      onChange: onChangeHandler(
        'max_value',
        setDisplayMax,
        setMaxIssue,
        value => minValue * budgetPeriod <= value,
        value => value / budgetPeriod,
      ),
      error: !!maxIssue,
      errorMessage: maxIssue,
      icon: 'dollar',
      iconPosition: 'left',
      disabled: mode === 'percentage',
    },
    {
      key: 'min_roas',
      value: displayRoas,
      placeholder: isConversionKPI ? `Max ${metricText}` : `Min ${metricText}`,
      onChange: onChangeHandler(
        'min_roas',
        setDisplayRoas,
        setRoasIssue,
        () => true,
        value => (isConversionKPI ? 1 / parseFloat(value) : Number(value)),
      ),
      error: !!roasIssue,
      errorMessage: roasIssue,
      icon: isConversionKPI ? 'dollar' : false,
      iconPosition: isConversionKPI ? 'left' : false,
    },
  ];

  return {
    inputCells,
  };
};
